import {useEffect, useState} from 'react';

interface BlobUrlState {
  loading: boolean;
  blobUrl: string | null;
  error: boolean;
}

interface InternalState extends BlobUrlState {
  forUrl: string | null;
}

// Fetches a signed file URL and exposes it as a same-origin blob: URL so
// embedders (Netron) never see the signed URL itself. The object URL is
// revoked when the source URL changes or the component unmounts.
export function useBlobUrl(url: string | null | undefined): BlobUrlState {
  // forUrl records which URL the state describes so a render where `url` has
  // changed but the effect has not run yet still reports loading, instead of
  // flashing the error state for a commit.
  const [state, setState] = useState<InternalState>({
    forUrl: null,
    loading: false,
    blobUrl: null,
    error: false,
  });

  useEffect(() => {
    if (url == null) {
      setState({forUrl: null, loading: false, blobUrl: null, error: false});
      return;
    }
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setState({forUrl: url, loading: true, blobUrl: null, error: false});
    (async () => {
      try {
        const resp = await fetch(url, {signal: controller.signal}); // oxlint-disable-line wandb/no-unprefixed-urls
        if (!resp.ok) {
          throw new Error(`Failed to fetch file: ${resp.status}`);
        }
        const blob = await resp.blob();
        // blob() can resolve after cleanup already aborted: bail before
        // creating an object URL that would never be revoked.
        if (controller.signal.aborted) {
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setState({
          forUrl: url,
          loading: false,
          blobUrl: objectUrl,
          error: false,
        });
      } catch {
        if (!controller.signal.aborted) {
          setState({forUrl: url, loading: false, blobUrl: null, error: true});
        }
      }
    })();
    return () => {
      controller.abort();
      if (objectUrl != null) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [url]);

  return {
    loading: url != null && (state.forUrl !== url || state.loading),
    blobUrl: state.forUrl === url ? state.blobUrl : null,
    error: state.forUrl === url ? state.error : false,
  };
}
